import React, { useCallback, useMemo, useState } from 'react';
import { ngContext } from '../../../../core/services/ng.delegate';
import utilSvc from '../../../../core/utilSvc';
import { ValidOption } from '../../../../core/components/validation/types';
import ValidInputMultiSelect, {
    validPropMultiSelectVM,
} from '../../../../core/components/validation/ValidInputMultiSelect';
import ValidCheckbox from '../../../../core/components/validation/ValidCheckbox';
import FormEdit from '../../../../core/components/form/FormEdit';
import FormGroup from '../../../../core/components/form/FormGroup';
import Button from '../../../../core/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { DatafeedQuery } from '../../data/datamonitorRepo';

// Full legacy Verity monitor-type list (sorted) with a leading "ALL" (= no type filter).
const MONITOR_TYPES: string[] = [
    'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED',
    'AFFILIATION.CATALOG.RECEIVED',
    'AFFILIATION.INVOICES.RECEIVED',
    'AFFILIATION.SPLITORDER.RECEIVED',
    'AFFILIATION.SPLITTRANSACTIONS.RECEIVED',
    'COVEREDENTITY.CDC.RESTOCK',
    'COVEREDENTITY.CDC.RESTOCK_EXCEPTION',
    'COVEREDENTITY.ENCOUNTERS.RECEIVED',
    'COVEREDENTITY.EPRESCRIBE.RECEIVED',
    'COVEREDENTITY.NPI.RECEIVED',
    'COVEREDENTITY.REFNPI.RECEIVED',
    'ORDER_IN',
    'ORDER_OUT',
    'PHARMACY.DISPENSE.RECEIVED',
    'SWITCH.DISPENSE.RECEIVED',
    'VENDOR.TPA.INVOICEFILE.RECEIVED',
    'VENDOR.TPA.MATCHES.SENT',
    'VENDOR.TPA.QBEXPORT',
    'VENDOR.TPA.RECONFILE',
];

type EntityProp = 'CE' | 'RX';

const droplistToOptions = (list: { label: string; value: string }[]): ValidOption[] =>
    list.map(d => ({ lbl: d.label, val: d.value }));

// Options carry no 'ALL' entry; the multiselect's built-in "All" toggles the full list.
// Collapse an all-selected list to the 'ALL' sentinel the datafeed API expects (= no filter).
const collapseToAll = (vals: string[], options: ValidOption[]): string[] =>
    vals.length > 0 && vals.length === options.length ? ['ALL'] : vals;

type MonitorStatusSearchFormProps = {
    waiting: boolean;
    onSearch: (query: DatafeedQuery) => void;
    // In-app alert deep-link: pre-select this MonitorType and turn the In-App filter on.
    initialMonitorType?: string | null;
};

const MonitorStatusSearchForm = ({
    waiting,
    onSearch,
    initialMonitorType,
}: MonitorStatusSearchFormProps): JSX.Element => {
    const affs = useMemo(() => {
        const ctx = ngContext();
        return utilSvc.aff.findStatus(ctx.User?.AffList ?? []);
    }, []);

    const [primaryProp, setPrimaryProp] = useState<EntityProp>('CE');
    const [ceVals, setCeVals] = useState<string[]>(['ALL']);
    const [rxVals, setRxVals] = useState<string[]>(['ALL']);
    const [monitorTypeVals, setMonitorTypeVals] = useState<string[]>(
        initialMonitorType ? [initialMonitorType] : ['ALL'],
    );
    const [status, setStatus] = useState({ PASS: true, FAIL: true, OFF: true });
    const [alerts, setAlerts] = useState({ email: false, inApp: !!initialMonitorType });
    const [errorMsg, setErrorMsg] = useState('');

    const primaryVals = primaryProp === 'CE' ? ceVals : rxVals;
    const secondaryVals = primaryProp === 'CE' ? rxVals : ceVals;

    // Primary options are the full CE (or RX) list; secondary options are filtered by the primary selection.
    const primaryOptions = useMemo<ValidOption[]>(() => {
        const list =
            primaryProp === 'CE'
                ? utilSvc.aff.ceDroplist(affs, undefined, false, false)
                : utilSvc.aff.rxDroplist(affs, undefined, false, false);
        return droplistToOptions(list);
    }, [affs, primaryProp]);

    const secondaryOptions = useMemo<ValidOption[]>(() => {
        // 'ALL' is the pre-expansion sentinel; treat it (and a full selection) as "all facilities/pharmacies"
        const usingAll =
            primaryVals.length === 0 || primaryVals.includes('ALL') || primaryVals.length === primaryOptions.length;
        if (primaryProp === 'CE') {
            const base = usingAll ? affs : utilSvc.aff.findCeList(affs, primaryVals);
            return droplistToOptions(utilSvc.aff.rxDroplist(base, undefined, false, false));
        }
        const base = usingAll ? affs : utilSvc.aff.findRxList(affs, primaryVals);
        return droplistToOptions(utilSvc.aff.ceDroplist(base, undefined, false, false));
    }, [affs, primaryProp, primaryVals, primaryOptions.length]);

    const monitorTypeOptions = useMemo<ValidOption[]>(() => MONITOR_TYPES.map(t => ({ lbl: t, val: t })), []);

    const setPrimaryVals = useCallback(
        (vals: string[]): void => {
            if (primaryProp === 'CE') setCeVals(vals);
            else setRxVals(vals);
        },
        [primaryProp],
    );

    const setSecondaryVals = useCallback(
        (vals: string[]): void => {
            if (primaryProp === 'CE') setRxVals(vals);
            else setCeVals(vals);
        },
        [primaryProp],
    );

    const handlePrimarySelect = useCallback(
        (vals: (string | number)[]): void => {
            setPrimaryVals(vals.map(String));
            // Reset the dependent secondary selection when the primary scope changes.
            setSecondaryVals(['ALL']);
        },
        [setPrimaryVals, setSecondaryVals],
    );

    const handleSecondarySelect = useCallback(
        (vals: (string | number)[]): void => {
            setSecondaryVals(vals.map(String));
        },
        [setSecondaryVals],
    );

    const handleSwap = useCallback((): void => {
        setPrimaryProp(prev => (prev === 'CE' ? 'RX' : 'CE'));
    }, []);

    const buildQuery = useCallback((): DatafeedQuery | null => {
        const statusList: string[] = [];
        if (status.PASS) statusList.push('PASS');
        if (status.FAIL) statusList.push('FAIL');
        if (status.OFF) statusList.push('OFF');

        if (ceVals.length < 1 || rxVals.length < 1 || monitorTypeVals.length < 1 || statusList.length < 1) {
            setErrorMsg('Facility, Pharmacy, Monitor Type, and at least one Status are required.');
            return null;
        }
        setErrorMsg('');
        const ceOptions = primaryProp === 'CE' ? primaryOptions : secondaryOptions;
        const rxOptions = primaryProp === 'CE' ? secondaryOptions : primaryOptions;
        return {
            CE: collapseToAll(ceVals, ceOptions),
            RX: collapseToAll(rxVals, rxOptions),
            MonitorType: collapseToAll(monitorTypeVals, monitorTypeOptions),
            Status: statusList,
            SubscribedEmail: alerts.email,
            SubscribedInApp: alerts.inApp,
        };
    }, [
        ceVals,
        rxVals,
        monitorTypeVals,
        status,
        alerts,
        primaryProp,
        primaryOptions,
        secondaryOptions,
        monitorTypeOptions,
    ]);

    const handleSearch = useCallback((): void => {
        const query = buildQuery();
        if (query) onSearch(query);
    }, [buildQuery, onSearch]);

    const primaryLabel = primaryProp === 'CE' ? 'Facility' : 'Pharmacy';
    const secondaryLabel = primaryProp === 'CE' ? 'Pharmacy' : 'Facility';

    return (
        <FormEdit
            formName="monitorStatusSearch"
            cssName="monitor-status-search"
            fieldsetId="monitor-status-search-fieldset"
        >
            <div className="fieldset-form">
                <div className="fieldset-inline">
                    <div className="fieldset-section monitor-search-entities">
                        <ValidInputMultiSelect
                            id="monitorPrimaryMulti"
                            prop={validPropMultiSelectVM('primary', primaryLabel, primaryVals, 'ALL', primaryOptions)}
                            onOptionSelect={handlePrimarySelect}
                            disableAllSelection
                            placeholder={`Choose a ${primaryLabel}`}
                        />
                        {/* legacy hides the secondary filter + swap until the primary has a selection */}
                        {primaryVals.length > 0 ? (
                            <>
                                <ValidInputMultiSelect
                                    id="monitorSecondaryMulti"
                                    prop={validPropMultiSelectVM(
                                        'secondary',
                                        secondaryLabel,
                                        secondaryVals,
                                        'ALL',
                                        secondaryOptions,
                                    )}
                                    onOptionSelect={handleSecondarySelect}
                                    disableAllSelection
                                    labelNoMinWidth
                                    placeholder={`Choose a ${secondaryLabel}`}
                                />
                                <Button
                                    id="monitorSwapBtn"
                                    cssName="btn-cancel monitor-search-swap"
                                    onClick={handleSwap}
                                >
                                    <FontAwesomeIcon icon={faRightLeft} />
                                    <span>Filter Swap</span>
                                </Button>
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="fieldset-inline">
                    <div className="fieldset-section">
                        <ValidInputMultiSelect
                            id="monitorTypeMulti"
                            prop={validPropMultiSelectVM(
                                'monitorType',
                                'Monitor Type',
                                monitorTypeVals,
                                'ALL',
                                monitorTypeOptions,
                            )}
                            onOptionSelect={vals => setMonitorTypeVals(vals.map(String))}
                            disableAllSelection
                            textInputWidth={400}
                            placeholder="Choose a Monitor Type"
                        />
                    </div>
                </div>

                <div className="fieldset-inline monitor-search-checks">
                    <FormGroup title="Status">
                        <div className="monitor-search-check-row">
                            <ValidCheckbox
                                id="monitorChkPass"
                                label="Pass"
                                checked={status.PASS}
                                onChange={val => setStatus(s => ({ ...s, PASS: val }))}
                                alignLeft
                            />
                            <ValidCheckbox
                                id="monitorChkFail"
                                label="Fail"
                                checked={status.FAIL}
                                onChange={val => setStatus(s => ({ ...s, FAIL: val }))}
                                alignLeft
                            />
                            <ValidCheckbox
                                id="monitorChkOff"
                                label="Off"
                                checked={status.OFF}
                                onChange={val => setStatus(s => ({ ...s, OFF: val }))}
                                alignLeft
                            />
                        </div>
                    </FormGroup>

                    <FormGroup title="My Alerts">
                        <div className="monitor-search-check-row">
                            <ValidCheckbox
                                id="monitorChkEmail"
                                label="Email"
                                checked={alerts.email}
                                onChange={val => setAlerts(a => ({ ...a, email: val }))}
                                alignLeft
                            />
                            <ValidCheckbox
                                id="monitorChkInApp"
                                label="In App"
                                checked={alerts.inApp}
                                onChange={val => setAlerts(a => ({ ...a, inApp: val }))}
                                alignLeft
                            />
                        </div>
                    </FormGroup>
                </div>

                <div className="fieldset-inline monitor-search-actions">
                    <div className="form-field">
                        <Button
                            id="monitorSearchBtn"
                            cssName="btn-submit"
                            onClick={handleSearch}
                            disabled={waiting}
                        >
                            Search
                        </Button>
                    </div>
                </div>

                {errorMsg ? <div className="monitor-search-error valid-input-error">{errorMsg}</div> : null}
            </div>
        </FormEdit>
    );
};

export default MonitorStatusSearchForm;
